import { Controller } from "@hotwired/stimulus"

// Zeitleiste selection mode: while active, clicks on timeline entries toggle a
// server-side (session-persisted) selection instead of opening the detail
// offcanvas. The capture-phase listener wins over the entries' own
// timeline-entry#openDetails action, and does nothing at all while the mode is
// off, so quick-edit/details behave normally then. The selected ids are
// server-rendered into selectedValue, so a page refresh restores highlights.
//
// Usage: data-controller="timeline-select"
//        data-timeline-select-active-value="true|false"
//        data-timeline-select-selected-value="[1,2,3]"
//        data-timeline-select-url-value="/configurable_time_sheets/timeline_selection"
export default class extends Controller {
  static targets = ["count", "duplicateButton"]
  static values = { active: Boolean, selected: Array, url: String }

  connect() {
    if (!this.activeValue) return

    this.applyHighlights()
    this.boundCaptureClick = this.captureClick.bind(this)
    this.element.addEventListener("click", this.boundCaptureClick, true)
  }

  disconnect() {
    if (this.boundCaptureClick) {
      this.element.removeEventListener("click", this.boundCaptureClick, true)
    }
  }

  captureClick(event) {
    const entry = event.target.closest(
      "[data-timeline-select-id], [data-timeline-select-ids], [data-timeline-select-inert]",
    )
    if (!entry) return

    event.preventDefault()
    event.stopPropagation()

    // Clickable but entry-less elements (absence-only month cells): swallow the
    // click so selection mode never navigates away.
    const ids = this.elementIds(entry)
    if (ids.length === 0) return

    // A cell with several entries toggles as one unit: complete a partial
    // selection first, clear only once everything is selected.
    const allSelected = ids.every((id) => this.selectedValue.includes(id))
    const op = allSelected ? "remove" : "add"
    const entryIds = allSelected ? ids : ids.filter((id) => !this.selectedValue.includes(id))

    fetch(this.urlValue, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
      },
      body: JSON.stringify({ entry_ids: entryIds, op: op }),
    })
      .then((response) => response.json())
      .then(({ count, selected }) => {
        this.selectedValue = selected
        this.applyHighlights()
        this.updateBar(count)
      })
      .catch((error) => {
        console.error("Failed to update timeline selection:", error)
      })
  }

  elementIds(el) {
    if (el.dataset.timelineSelectId) return [parseInt(el.dataset.timelineSelectId, 10)]
    if (el.dataset.timelineSelectIds) return JSON.parse(el.dataset.timelineSelectIds)
    return []
  }

  applyHighlights() {
    this.element
      .querySelectorAll("[data-timeline-select-id], [data-timeline-select-ids]")
      .forEach((el) => {
        const ids = this.elementIds(el)
        const selectedCount = ids.filter((id) => this.selectedValue.includes(id)).length
        const full = selectedCount === ids.length && ids.length > 0
        const partial = selectedCount > 0 && !full
        el.classList.toggle("ring-2", full || partial)
        el.classList.toggle("ring-green-500", full)
        el.classList.toggle("ring-green-300", partial)
      })
  }

  updateBar(count) {
    if (this.hasCountTarget) this.countTarget.textContent = count
    if (this.hasDuplicateButtonTarget) this.duplicateButtonTarget.disabled = count === 0
  }
}
